import * as QueryString from 'query-string';
import {useEffect} from 'react';
import {useHistory, useLocation} from 'react-router-dom';

import {type ViewType} from '../../components/Workspace/multiRunWorkspaceHooks';
import {type RunPagePersistentState} from '../../components/Workspace/singleRunWorkspaceHooks';
import {useDispatch, useMemoizedSelector} from '../hooks';
import {type ProjectPageViewSpec} from '../views/projectPage/types';
import {makeViewListSelector} from '../views/selectors';
import {useWorkspaceInit} from './hooks';
import {loadNamedWorkspaceForUrl} from './namedWorkspacesActions';
import {
  getSafeNwPart,
  getWorkspaceType,
  viewIsDeletedNW,
  viewIsLegacyWorkspace,
} from './names';
import {
  getDataMaybe,
  getStatus,
  makeWorkspaceReduxStateWithViewSelector,
} from './selectors';
import {
  getIsPersonalWorkspace,
  getUsernameFromId,
  type NamedWorkspaceId,
  WorkspaceTypes,
} from './types';
import {ParamNames, type ParamNameType, type WorkspaceUrl} from './urlTypes';

export {getNewNwUrlSearch, handleNwHistoryNavigation} from './urlNavigation';
export {ParamNames, type ParamNameType, type WorkspaceUrl} from './urlTypes';

// These are all legacy and not used in named workspaces
/** @deprecated */
const WorkspaceParamValues = {
  empty: 'empty',
  default: 'default',
  userPrefix: 'user-',
} as const;

/**
 * This function is responsible for
 * 1. populating redux with workspace information so we can display specified workspace and list of views
 * 2. handles url changes
 *    a. if specified url is invalid, redirect to a valid url
 *    b. if url param changed and the corresponding data is available, go load it
 */
export function useInitWorkspaceAndHandleUrlChanges(args: {
  entityName: string;
  projectName: string;
  viewType: ViewType | 'run-view';
  defaultSpec: ProjectPageViewSpec | RunPagePersistentState;
  workspaceObjectID?: string;
}) {
  const location = useLocation();
  const workspaceUrl = parsedOrRedirectedWorkspaceUrl(location.search);
  const workspaceStateId = useWorkspaceInit(
    args.entityName,
    args.projectName,
    args.viewType,
    args.defaultSpec,
    workspaceUrl,
    args.workspaceObjectID
  );

  useHandleNwUrl({
    entityName: args.entityName,
    projectName: args.projectName,
    viewType: args.viewType,
    workspaceStateId,
    workspaceUrl,
  });

  return workspaceStateId;
}

/**
 *
 * Responsible for updating redux when the browser's URL bar changes.
 */
export function useHandleNwUrl({
  entityName,
  projectName,
  viewType,
  workspaceStateId,
  workspaceUrl,
}: {
  entityName: string;
  projectName: string;
  viewType: ViewType | 'run-view';
  workspaceStateId: string;
  workspaceUrl: WorkspaceUrl;
}) {
  const history = useHistory();
  const dispatch = useDispatch();

  const workspaceResult = useMemoizedSelector(
    makeWorkspaceReduxStateWithViewSelector,
    workspaceStateId
  );

  const workspaceData = getDataMaybe(workspaceResult);
  const nwId = workspaceData?.nwId;

  const viewListResult = useMemoizedSelector(
    makeViewListSelector,
    entityName,
    projectName,
    viewType
  );

  useEffect(() => {
    const isDataReady =
      !viewListResult.loading && getStatus(workspaceResult) === 'ready';

    // Only load a workspace when the URL points to a different named workspace.
    // Changes to other search parameters should leave the current workspace alone.
    const hasNwChanged =
      history.location.pathname === `/${entityName}/${projectName}` &&
      workspaceUrl.type === 'named' &&
      workspaceUrl.id !== nwId?.id;

    // Links push a new URL, while the workspace replaces the URL after it loads.
    // Ignore that replacement so it does not start another load.
    const didHistoryChange = history.action !== 'REPLACE';

    if (isDataReady && hasNwChanged && didHistoryChange) {
      dispatch(
        loadNamedWorkspaceForUrl(
          workspaceStateId,
          workspaceUrl,
          viewListResult.views,
          viewType
        )
      );
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [history.location.search]);
}

export function parsedOrRedirectedWorkspaceUrl(
  searchParams: URL['search']
): WorkspaceUrl {
  const parsed = QueryString.parse(searchParams);

  const workspaceParam = parsed[ParamNames.workspace];
  const namedParam = parsed[ParamNames.nwId];

  if (!parsed || Object.keys(parsed).length === 0) {
    return {type: 'none'};
  }

  if (namedParam != null) {
    return readOrRedirectNamedWorkspaceUrl(parsed, namedParam);
  }

  if (workspaceParam != null) {
    const paramValue = firstVal(workspaceParam);
    if (paramValue === WorkspaceParamValues.empty) {
      // This will load an empty workspace. Useful for getting out of broken states
      return {
        type: 'empty',
      };
    }
    if (paramValue === WorkspaceParamValues.default) {
      return {
        type: 'default',
      };
    }
    if (paramValue?.startsWith(WorkspaceParamValues.userPrefix)) {
      return {
        type: 'user',
        user: paramValue?.slice(WorkspaceParamValues.userPrefix.length),
      };
    }
  }

  return {
    type: 'none',
  };
}

const firstVal = (paramVal: string | string[]): string => {
  return Array.isArray(paramVal) ? paramVal[0] : paramVal;
};

// nw=<id>&published => (legacy) published workspace
// nw=<id>&user=<username> => (legacy) draft workspace
// nw=<id> => (new) shared and personal workspaces
const readOrRedirectNamedWorkspaceUrl = (
  parsed: QueryString.ParsedQuery<string>,
  namedParam: string | string[]
): WorkspaceUrl => {
  const usernameParam = parsed[ParamNames.username];
  const id = firstVal(namedParam);

  const username = usernameParam ? firstVal(usernameParam) : undefined;
  const parsedUsername = username == null ? getUsernameFromId(id) : username;

  if (getIsPersonalWorkspace(id) && parsedUsername != null) {
    return {
      type: 'named',
      id,
      nwType: 'personal workspace',
      // we don't use safeNwUsername here because the URL should not have been created with the `-` in the first place
      username: parsedUsername,
    };
  }

  if (parsedUsername == null) {
    return {
      type: 'named',
      id,
      nwType: 'shared workspace',
    };
  }

  return {type: 'none'};
};

/**
 * calculate the correct URL string for named workspaces
 */
export function getNWUrlParams(
  workspaceNwId: NamedWorkspaceId | undefined,
  viewName: string | undefined,
  viewUsername: string | undefined
): Partial<Record<ParamNameType, null | string>> {
  if (viewName == null || viewUsername == null || viewIsDeletedNW(viewName)) {
    return {};
  }

  if (workspaceNwId) {
    if (
      viewIsLegacyWorkspace(workspaceNwId.id) &&
      workspaceNwId.nwType === 'personal workspace'
    ) {
      /**
       * This creates the equivalent nwId from a legacy view. The view.name value is different
       * for NW and legacy views.
       */
      return {
        [ParamNames.nwId]: getNWParamForLegacyUserWorkspace(
          workspaceNwId.username,
          workspaceNwId.relatedId
        ),
      };
    }
    return {
      [ParamNames.nwId]: workspaceNwId.id,
    };
  }

  // Handles updating to a legacy workspace url
  const type = getWorkspaceType(viewName);
  return {
    [ParamNames.workspace]:
      type === WorkspaceTypes.default
        ? WorkspaceParamValues.default
        : WorkspaceParamValues.userPrefix + viewUsername,
  };
}

const getNWParamForLegacyUserWorkspace = (
  username: string,
  relatedId?: string
) => {
  const workspaceNameSuffix = relatedId != null ? `-${relatedId}` : '';
  return getSafeNwPart(`nwuser${username}`) + workspaceNameSuffix;
};

/** Gets current nwId from URL and returns as search string */
export const getNwIdSearchString = (): string => {
  const allParams = new URLSearchParams(window.location.search);
  const nwId = allParams.get(ParamNames.nwId);
  return nwId ? `?${ParamNames.nwId}=${nwId}` : '';
};
